import { useState } from 'react';
import SourceLink from './SourceLink';
import { AlertTriangle, FileText, Plug, Sigma } from 'lucide-react';
import type { FieldOrigin } from '../lib/types';

// Where an answer or a figure came from. Each provenance gets its own label, so a
// claim read off a document stays visibly a claim.
export function OriginLine({ origin, status }: {
    origin: FieldOrigin | null; status: FieldOrigin['kind'] | 'unknown';
}) {
    if (status === 'derived') {
        return (
            <>
                <Plug className="w-3 h-3 shrink-0" />
                {origin?.source ? (
                    <>From <SourceLink source={origin.source} className="text-brand hover:text-brand-dark">
                        {origin.origin_name || 'a connected source'}
                    </SourceLink></>
                ) : 'From a connected source'}
            </>
        );
    }
    if (status === 'computed') {
        return (
            <>
                <Sigma className="w-3 h-3 shrink-0" />
                {origin?.origin_name
                    ? `Worked out from ${origin.origin_name}`
                    : 'Worked out from your figures'}
            </>
        );
    }
    if (status === 'extracted') {
        return (
            <>
                <FileText className="w-3 h-3 shrink-0" />
                {origin?.source ? (
                    <>Interpreted from <SourceLink source={origin.source} className="text-brand hover:text-brand-dark">
                        {origin.origin_name || 'a document'}
                    </SourceLink></>
                ) : 'Interpreted from a document'}
            </>
        );
    }
    return <>{origin?.origin_name ? `Stated by ${origin.origin_name}` : 'Stated'}</>;
}

// A correction is sticky; when the system it corrects later moves to a new value,
// say so and offer the choice. Two sources describing the same date differently
// leave the answer to the person.
export function ConflictNotice({ origin, status, theirs, onUseTheirs }: {
    origin: FieldOrigin | null;
    status: FieldOrigin['kind'] | 'unknown';
    theirs: string;
    onUseTheirs: () => Promise<void>;
}) {
    const conflict = origin?.conflict;
    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    if (!conflict || dismissed) return null;

    const who = conflict.origin_name
        || (conflict.kind === 'extracted' ? 'A document' : 'The connected source');

    if (status !== 'stated') {
        return (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[12px] text-amber-900">
                    {who} gives <strong className="break-words">{theirs}</strong> for the same date.
                    Correct this to say which is right.
                </p>
            </div>
        );
    }

    const useTheirs = async () => {
        setBusy(true);
        try {
            await onUseTheirs();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
                <p className="text-[12px] text-amber-900">
                    {who} now says <strong className="break-words">{theirs}</strong>.
                    Your answer is being used instead.
                </p>
                <div className="mt-1.5 flex flex-wrap gap-3">
                    <button
                        onClick={useTheirs}
                        disabled={busy}
                        className="text-[12px] font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700 disabled:opacity-50"
                    >
                        {busy ? 'Updating...' : 'Use theirs'}
                    </button>
                    <button
                        onClick={() => setDismissed(true)}
                        className="text-[12px] font-medium text-amber-900/70 hover:text-amber-900"
                    >
                        Keep mine
                    </button>
                </div>
            </div>
        </div>
    );
}
